import React, {useState, useEffect} from 'react';
import {View, StyleSheet} from 'react-native';
import {Modal, Portal, Text, TextInput, Button} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useInventoryCatalogStore} from '../../store/useInventoryCatalogStore';
import DateTimePicker from '@react-native-community/datetimepicker';
import {colors, spacing} from '../../theme';

const EditPurchaseModal = ({visible, item, onDismiss, onSuccess}) => {
  const {t} = useTranslation();
  const {updatePurchaseOrder} = useInventoryCatalogStore();
  const [quantity, setQuantity] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (item) {
      // Pre-fill from first line if available
      const firstLine = item.lines && item.lines.length > 0 ? item.lines[0] : null;
      setQuantity(firstLine ? String(firstLine.quantity || '') : '');
      setUnitPrice(firstLine ? String(firstLine.unitPrice || '') : '');
      setPurchaseDate(item.orderDate ? new Date(item.orderDate) : new Date());
    }
  }, [item]);

  const handleSave = async () => {
    if (!quantity || !unitPrice) {
      setError(t('validation.required'));
      return;
    }

    setLoading(true);
    setError('');
    try {
      const firstLine = item.lines && item.lines.length > 0 ? item.lines[0] : null;
      await updatePurchaseOrder(item.id, {
        lines: [{
          catalogItemId: firstLine ? firstLine.catalogItemId : null,
          quantity: parseInt(quantity, 10),
          unitPrice: parseFloat(unitPrice),
        }],
        purchaseDate: purchaseDate.toISOString().split('T')[0],
      });
      onSuccess();
    } catch (e) {
      setError(e.response?.data?.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const formatDisplay = (date) => date.toLocaleDateString('en-IN', {day: '2-digit', month: 'short', year: 'numeric'});

  if (!item) return null;

  const firstLine = item.lines && item.lines.length > 0 ? item.lines[0] : null;
  const itemName = firstLine ? firstLine.catalogItemName : '';
  const sku = firstLine ? firstLine.sku : null;

  return (
    <Portal>
      <Modal visible={visible} onDismiss={onDismiss} contentContainerStyle={styles.modal}>
        <Text style={styles.title}>{t('inventory.editPurchase')}</Text>

        <TextInput
          label={t('inventory.itemName')}
          value={itemName}
          disabled
          mode="outlined"
          style={styles.input}
        />

        {sku && (
          <TextInput
            label={t('inventory.sku')}
            value={sku}
            disabled
            mode="outlined"
            style={styles.input}
          />
        )}

        <TextInput
          label={t('inventory.purchaseQty')}
          value={quantity}
          onChangeText={setQuantity}
          keyboardType="decimal-pad"
          mode="outlined"
          style={styles.input}
        />

        <TextInput
          label={t('inventory.purchaseUnitPrice')}
          value={unitPrice}
          onChangeText={setUnitPrice}
          keyboardType="decimal-pad"
          mode="outlined"
          left={<TextInput.Affix text="\u20B9" />}
          style={styles.input}
        />

        <Button
          mode="outlined"
          onPress={() => setShowDatePicker(true)}
          style={styles.dateButton}>
          {t('inventory.purchaseDate')}: {formatDisplay(purchaseDate)}
        </Button>

        {showDatePicker && (
          <DateTimePicker
            value={purchaseDate}
            mode="date"
            onChange={(e, date) => {
              setShowDatePicker(false);
              if (date) setPurchaseDate(date);
            }}
          />
        )}

        {error ? <Text style={styles.error}>{error}</Text> : null}

        <View style={styles.actions}>
          <Button mode="outlined" onPress={onDismiss} style={styles.actionBtn}>
            {t('common.cancel')}
          </Button>
          <Button mode="contained" onPress={handleSave} loading={loading} style={styles.actionBtn}>
            {t('common.save')}
          </Button>
        </View>
      </Modal>
    </Portal>
  );
};

const styles = StyleSheet.create({
  modal: {
    backgroundColor: colors.surface,
    margin: spacing.md,
    padding: spacing.md,
    borderRadius: 15,
  },
  title: {fontSize: 18, fontWeight: '600', marginBottom: spacing.md, color: colors.text},
  input: {marginBottom: spacing.sm},
  dateButton: {marginBottom: spacing.sm},
  error: {color: colors.error, marginBottom: spacing.sm, fontSize: 13},
  actions: {flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.sm},
  actionBtn: {minWidth: 100, borderRadius: 10, paddingHorizontal: 10},
});

export default EditPurchaseModal;
